/**
 * Investment Match Dialog Component - ENHANCED
 * 
 * Now shows:
 * 1. All active investments (not just pending transactions)
 * 2. Transaction type selector (Interest, Dividend, Withdrawal, etc.)
 * 3. Existing pending investment transactions if available
 * 
 * This allows proper matching of bank transactions to investments.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Card, CardContent } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import {
  Loader2,
  TrendingUp,
  Building2,
  Calendar,
  DollarSign,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ArrowDownLeft,
  ArrowUpRight,
  Sparkles,
  PiggyBank,
  Landmark,
  Percent,
  Banknote
} from 'lucide-react';

// Transaction type labels
const TRANSACTION_TYPE_LABELS = {
  interest_accrual: 'Interest Accrual',
  interest_inflow: 'Interest Receipt',
  dividend: 'Dividend',
  rental_income: 'Rental Income',
  maturity: 'Maturity Proceeds',
  partial_liquidation: 'Partial Withdrawal',
  full_liquidation: 'Full Liquidation',
  top_up: 'Top-Up',
  fair_value_adjustment: 'Fair Value Adjustment',
  purchase: 'New Purchase'
};

// Investment type labels
const INVESTMENT_TYPE_LABELS = {
  fixed_deposit: 'Fixed Deposit',
  treasury_bill: 'Treasury Bill',
  bonds: 'Bonds',
  money_market: 'Money Market',
  mutual_funds: 'Mutual Funds',
  stocks: 'Stocks',
  real_estate: 'Real Estate',
  other: 'Other'
};

export const InvestmentMatchDialog = ({
  open,
  onOpenChange,
  bankTransaction,
  formatAmount,
  onMatchComplete,
  bankCurrency,
  baseCurrency
}) => {
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [selectedInvestmentId, setSelectedInvestmentId] = useState(null);
  const [selectedTransactionType, setSelectedTransactionType] = useState(null);
  const [selectedPendingTxId, setSelectedPendingTxId] = useState(null);
  const [matchMode, setMatchMode] = useState('investment'); // 'investment' or 'pending_tx'

  // Fetch potential matches when dialog opens
  useEffect(() => {
    if (open && bankTransaction?.id) {
      fetchPotentialMatches();
    } else {
      setMatchData(null);
      setSelectedInvestmentId(null);
      setSelectedTransactionType(null);
      setSelectedPendingTxId(null);
      setMatchMode('investment');
    }
  }, [open, bankTransaction?.id]);

  const fetchPotentialMatches = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/bank-feeds/investment-matches/${bankTransaction.id}`);
      setMatchData(response.data);
      
      // Auto-select best match if confidence is high
      if (response.data?.best_match?.match_source === 'investment_register') {
        // iter1067 - register-level suggestion (no pending expected
        // transaction): preselect the investment + suggested type directly.
        setSelectedInvestmentId(response.data.best_match.investment_id);
        setSelectedTransactionType(response.data.best_match.transaction_type || (response.data?.transaction_type === 'inflow' ? 'partial_liquidation' : 'top_up'));
        setMatchMode('investment');
        return;
      }
      if (response.data?.best_match?.confidence_score >= 70) {
        setSelectedPendingTxId(response.data.best_match.investment_transaction_id);
        setMatchMode('pending_tx');
      }
      
      // Default transaction type based on flow direction
      if (response.data?.transaction_type === 'inflow') {
        setSelectedTransactionType('interest_inflow');
      } else {
        setSelectedTransactionType('top_up');
      }
    } catch (error) {
      console.error('Error fetching investment matches:', error);
      toast.error('Failed to load potential investment matches');
    } finally {
      setLoading(false);
    }
  };

  const handleMatchToInvestment = async () => {
    if (matchMode === 'pending_tx') {
      // Match to existing pending transaction
      if (!selectedPendingTxId) {
        toast.error('Please select a pending transaction to match');
        return;
      }
      
      setProcessing(true);
      try {
        const formData = new FormData();
        formData.append('investment_transaction_id', selectedPendingTxId);
        formData.append('actual_amount', Math.abs(bankTransaction.amount));
        formData.append('not_an_investment', 'false');

        await axios.post(
          `${API}/bank-feeds/review/${bankTransaction.id}/investment-match`,
          formData);

        toast.success('Transaction matched to investment successfully');
        onMatchComplete?.();
        onOpenChange(false);
      } catch (error) {
        console.error('Error matching to investment:', error);
        toast.error(error.response?.data?.detail || 'Failed to match transaction');
      } finally {
        setProcessing(false);
      }
    } else {
      // Match to investment with new transaction type
      if (!selectedInvestmentId) {
        toast.error('Please select an investment');
        return;
      }
      if (!selectedTransactionType) {
        toast.error('Please select a transaction type');
        return;
      }
      
      setProcessing(true);
      try {
        const formData = new FormData();
        formData.append('investment_id', selectedInvestmentId);
        formData.append('transaction_type', selectedTransactionType);
        formData.append('actual_amount', Math.abs(bankTransaction.amount));
        formData.append('not_an_investment', 'false');

        await axios.post(
          `${API}/bank-feeds/review/${bankTransaction.id}/investment-match`,
          formData);

        toast.success('Transaction matched to investment successfully');
        onMatchComplete?.();
        onOpenChange(false);
      } catch (error) {
        console.error('Error matching to investment:', error);
        toast.error(error.response?.data?.detail || 'Failed to match transaction');
      } finally {
        setProcessing(false);
      }
    }
  };

  const handleNotAnInvestment = async () => {
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('investment_transaction_id', '');
      formData.append('not_an_investment', 'true');

      await axios.post(
        `${API}/bank-feeds/review/${bankTransaction.id}/investment-match`,
        formData);

      toast.success('Transaction marked as non-investment and returned for re-classification');
      onMatchComplete?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error marking as non-investment:', error);
      toast.error(error.response?.data?.detail || 'Failed to update transaction');
    } finally {
      setProcessing(false);
    }
  };

  const getInvestmentTypeBadge = (type) => {
    const colors = {
      fixed_deposit: 'bg-blue-100 text-blue-700',
      treasury_bill: 'bg-green-100 text-green-700',
      bonds: 'bg-purple-100 text-purple-700',
      money_market: 'bg-cyan-100 text-cyan-700',
      mutual_funds: 'bg-amber-100 text-amber-700',
      stocks: 'bg-rose-100 text-rose-700',
      real_estate: 'bg-teal-100 text-teal-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const isInflow = bankTransaction?.direction === 'inflow' || bankTransaction?.amount > 0;
  const hasInvestments = matchData?.investments?.length > 0;
  const hasPendingTx = matchData?.pending_transactions?.length > 0 || (matchData?.best_match && matchData.best_match.match_source !== 'investment_register');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PiggyBank className="w-5 h-5 text-emerald-600" />
            Match to Investment
          </DialogTitle>
          <DialogDescription>
            Select an investment and specify the transaction type, or match to an existing pending transaction.
          </DialogDescription>
        </DialogHeader>

        {/* Bank Transaction Summary */}
        {bankTransaction && (
          <div className="p-4 bg-muted/50 rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isInflow ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {isInflow 
                    ? <ArrowDownLeft className="w-4 h-4 text-green-600" />
                    : <ArrowUpRight className="w-4 h-4 text-red-600" />
                  }
                </div>
                <div>
                  <p className="font-medium text-sm">{bankTransaction.description?.slice(0, 50)}...</p>
                  <p className="text-xs text-muted-foreground">{bankTransaction.date}</p>
                </div>
              </div>
              <span className={`text-lg font-semibold ${
                isInflow ? 'text-green-600' : 'text-red-600'
              }`}>
                {isInflow ? '+' : '-'}{formatAmount?.(Math.abs(bankTransaction.amount)) || bankTransaction.amount?.toLocaleString()}
              </span>
            </div>
          </div>
        )}

        {/* FX Conversion Notice */}
        {bankCurrency && baseCurrency && bankCurrency !== baseCurrency && bankTransaction && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="fx-conversion-notice">
            <div className="flex items-center gap-2 text-amber-800 text-sm">
              <DollarSign className="w-4 h-4 shrink-0" />
              <span className="font-medium">Foreign Currency Transaction ({bankCurrency})</span>
            </div>
            <p className="text-xs text-amber-700 mt-1">
              Journal entries will be converted to {baseCurrency} at the current exchange rate.
            </p>
          </div>
        )}

        <Separator />

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            <span className="ml-3 text-muted-foreground">Loading investments...</span>
          </div>
        )}

        {/* Match Results */}
        {!loading && matchData && (
          <div className="flex-1 overflow-hidden space-y-4">
            
            {/* Mode Selection Tabs */}
            {(hasInvestments || hasPendingTx) && (
              <div className="flex gap-2 border-b pb-2">
                <Button
                  variant={matchMode === 'investment' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMatchMode('investment')}
                  className={matchMode === 'investment' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                >
                  <Landmark className="w-4 h-4 mr-2" />
                  Match to Investment ({matchData.investments?.length || 0})
                </Button>
                {hasPendingTx && (
                  <Button
                    variant={matchMode === 'pending_tx' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMatchMode('pending_tx')}
                    className={matchMode === 'pending_tx' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  >
                    <Banknote className="w-4 h-4 mr-2" />
                    Pending Transactions ({matchData.pending_transactions?.length || 0})
                  </Button>
                )}
              </div>
            )}

            {/* Investment Selection Mode */}
            {matchMode === 'investment' && (
              <>
                {hasInvestments ? (
                  <>
                    {/* Transaction Type Selector */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Transaction Type</Label>
                      <Select value={selectedTransactionType} onValueChange={setSelectedTransactionType}>
                        <SelectTrigger className="w-full" data-testid="transaction-type-select">
                          <SelectValue placeholder="Select transaction type" />
                        </SelectTrigger>
                        <SelectContent>
                          {matchData.available_transaction_types?.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex flex-col">
                                <span>{type.label}</span>
                                <span className="text-xs text-muted-foreground">{type.description}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Investment List */}
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Select Investment</Label>
                      <ScrollArea className="h-[250px] pr-4">
                        <RadioGroup 
                          value={selectedInvestmentId || ''} 
                          onValueChange={setSelectedInvestmentId}
                          className="space-y-2"
                        >
                          {matchData.investments?.map((inv) => (
                            <div
                              key={inv.id}
                              className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                selectedInvestmentId === inv.id 
                                  ? 'ring-2 ring-emerald-500 bg-emerald-50/50' 
                                  : 'hover:bg-muted/50'
                              }`}
                              onClick={() => setSelectedInvestmentId(inv.id)}
                              data-testid={`investment-option-${inv.id}`}
                            >
                              <RadioGroupItem value={inv.id} id={inv.id} />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Landmark className="w-4 h-4 text-emerald-600" />
                                  <span className="font-medium text-sm">{inv.investment_name}</span>
                                  <Badge className={`text-xs ${getInvestmentTypeBadge(inv.investment_type)}`}>
                                    {INVESTMENT_TYPE_LABELS[inv.investment_type] || inv.investment_type}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                  <span className="flex items-center gap-1">
                                    <Building2 className="w-3 h-3" />
                                    {inv.institution_name || 'N/A'}
                                  </span>
                                  {inv.interest_rate > 0 && (
                                    <span className="flex items-center gap-1">
                                      <Percent className="w-3 h-3" />
                                      {inv.interest_rate}% p.a.
                                    </span>
                                  )}
                                  {inv.maturity_date && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      Matures: {inv.maturity_date}
                                    </span>
                                  )}
                                </div>
                                {inv.accrued_interest > 0 && (
                                  <div className="mt-1 text-xs text-amber-600">
                                    Accrued Interest: {formatAmount?.(inv.accrued_interest) || inv.accrued_interest?.toLocaleString()}
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-sm">
                                  {formatAmount?.(inv.current_value) || inv.current_value?.toLocaleString()}
                                </p>
                                <p className="text-xs text-muted-foreground">Current Value</p>
                                {selectedInvestmentId === inv.id && (
                                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-1 ml-auto" />
                                )}
                              </div>
                            </div>
                          ))}
                        </RadioGroup>
                      </ScrollArea>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <h3 className="font-medium mb-1">No Active Investments Found</h3>
                    <p className="text-sm text-muted-foreground">
                      There are no active investments in the system.
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      You can mark this as "Not an Investment" to reclassify it.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Pending Transactions Mode */}
            {matchMode === 'pending_tx' && (
              <>
                {/* Best Match Suggestion */}
                {matchData.best_match && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-medium">Suggested Match</span>
                      <Badge variant="outline" className={
                        matchData.best_match.confidence_score >= 80 ? 'bg-green-50 text-green-700 border-green-200' :
                        matchData.best_match.confidence_score >= 60 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-gray-50 text-gray-700 border-gray-200'
                      }>
                        {matchData.best_match.confidence_score}% confidence
                      </Badge>
                    </div>
                    <Card 
                      className={`cursor-pointer transition-all ${
                        selectedPendingTxId === matchData.best_match.investment_transaction_id 
                          ? 'ring-2 ring-emerald-500 bg-emerald-50/50' 
                          : 'hover:border-emerald-300'
                      }`}
                      onClick={() => setSelectedPendingTxId(matchData.best_match.investment_transaction_id)}
                      data-testid="suggested-match-card"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Landmark className="w-4 h-4 text-emerald-600" />
                              <span className="font-medium">{matchData.best_match.investment_name}</span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {matchData.best_match.institution_name}
                            </p>
                            <Badge className={getInvestmentTypeBadge(matchData.best_match.investment_type)}>
                              {TRANSACTION_TYPE_LABELS[matchData.best_match.transaction_type] || matchData.best_match.transaction_type}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">
                              {formatAmount?.(matchData.best_match.expected_amount) || matchData.best_match.expected_amount?.toLocaleString()}
                            </p>
                            <p className="text-xs text-muted-foreground">Expected Amount</p>
                            {selectedPendingTxId === matchData.best_match.investment_transaction_id && (
                              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-2 ml-auto" />
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Other Pending Transactions */}
                {matchData.pending_transactions && matchData.pending_transactions.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium mb-2 block">
                      {matchData.best_match ? 'Other Pending Transactions' : 'Pending Transactions'}
                    </Label>
                    <ScrollArea className="h-[200px] pr-4">
                      <RadioGroup 
                        value={selectedPendingTxId || ''} 
                        onValueChange={setSelectedPendingTxId}
                        className="space-y-2"
                      >
                        {matchData.pending_transactions
                          .filter(tx => tx.id !== matchData.best_match?.investment_transaction_id)
                          .map((tx) => (
                            <div
                              key={tx.id}
                              className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                selectedPendingTxId === tx.id 
                                  ? 'ring-2 ring-emerald-500 bg-emerald-50/50' 
                                  : 'hover:bg-muted/50'
                              }`}
                              onClick={() => setSelectedPendingTxId(tx.id)}
                              data-testid={`pending-tx-${tx.id}`}
                            >
                              <RadioGroupItem value={tx.id} id={tx.id} />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{tx.investment_name}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {INVESTMENT_TYPE_LABELS[tx.investment_type] || tx.investment_type}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                  <span>{tx.institution_name || 'N/A'}</span>
                                  <Badge className={`text-xs ${getInvestmentTypeBadge(tx.investment_type)}`}>
                                    {TRANSACTION_TYPE_LABELS[tx.transaction_type] || tx.transaction_type}
                                  </Badge>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="font-medium">
                                  {formatAmount?.(tx.amount) || tx.amount?.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          ))}
                      </RadioGroup>
                    </ScrollArea>
                  </div>
                )}

                {!matchData.best_match && (!matchData.pending_transactions || matchData.pending_transactions.length === 0) && (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <h3 className="font-medium mb-1">No Pending Transactions</h3>
                    <p className="text-sm text-muted-foreground">
                      Switch to "Match to Investment" to select an investment directly.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <Separator />

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button
            variant="outline"
            onClick={handleNotAnInvestment}
            disabled={processing}
            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
            data-testid="not-investment-btn"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Not an Investment
          </Button>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMatchToInvestment}
              disabled={processing || (matchMode === 'investment' ? (!selectedInvestmentId || !selectedTransactionType) : !selectedPendingTxId)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="match-investment-btn"
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Match to Investment
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvestmentMatchDialog;
